/**
 * Google Calendar Recurrence Utilities
 *
 * Handles parsing and processing of Google Calendar recurring events
 * following RFC 5545 (iCalendar) RRULE format.
 */

import { rrulestr } from "rrule";

import type { ProcessedEvent } from "@/types/calendar";

/**
 * Parse Google Calendar RRULE string and get next occurrence
 *
 * @param rruleString - RFC 5545 RRULE string (e.g., "FREQ=WEEKLY;BYDAY=TH")
 * @param startDate - Original event start date
 * @param afterDate - Find occurrence after this date (defaults to now)
 * @returns Next occurrence date or null if no future occurrences
 */
export const getNextOccurrenceFromRRule = (
  rruleString: string,
  startDate: Date,
  afterDate: Date = new Date(),
): Date | null => {
  try {
    // Parse the RRULE string
    // Google Calendar format: "RRULE:FREQ=WEEKLY;BYDAY=TH"
    // We need to add the DTSTART for rrule library
    const dtstart = `DTSTART:${formatDateForRRule(startDate)}\n`;
    const fullRRule = rruleString.startsWith("RRULE:")
      ? `${dtstart}${rruleString}`
      : `${dtstart}RRULE:${rruleString}`;

    const rule = rrulestr(fullRRule);

    // Get the next occurrence after the specified date
    const nextOccurrence = rule.after(afterDate, false);

    return nextOccurrence;
  } catch (error) {
    console.error("Failed to parse RRULE:", rruleString, error);
    return null;
  }
};

/**
 * Format date for RRULE DTSTART format (YYYYMMDDTHHmmssZ)
 */
const formatDateForRRule = (date: Date): string => {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  const hours = String(date.getUTCHours()).padStart(2, "0");
  const minutes = String(date.getUTCMinutes()).padStart(2, "0");
  const seconds = String(date.getUTCSeconds()).padStart(2, "0");

  return `${year}${month}${day}T${hours}${minutes}${seconds}Z`;
};

/**
 * Get the next occurrence of a potentially recurring event
 *
 * @param event - The event to process
 * @param afterDate - Find occurrence after this date (defaults to now)
 * @returns Next occurrence date or null if no future occurrences
 */
export const getNextOccurrence = (
  event: ProcessedEvent,
  afterDate: Date = new Date(),
): Date | null => {
  // If event has recurrence rule, use it
  if (event.recurrenceRule) {
    return getNextOccurrenceFromRRule(
      event.recurrenceRule,
      event.start,
      afterDate,
    );
  }

  // If event is not recurring, return start date if it's in the future
  if (event.start > afterDate) {
    return event.start;
  }

  // Event is in the past and not recurring
  return null;
};

/**
 * Check if an event is recurring
 *
 * @param event - The event to check
 * @returns True if event has recurrence information
 */
export const isRecurringEvent = (event: ProcessedEvent): boolean => {
  return !!(event.recurrenceRule || event.isRecurring);
};

/**
 * Filter events to show only the next occurrence of recurring events
 * This prevents showing multiple instances of the same recurring event on the map
 *
 * @param events - Array of events to filter
 * @returns Filtered array with only next occurrences
 */
export const filterToNextOccurrences = (
  events: ProcessedEvent[],
): ProcessedEvent[] => {
  const now = new Date();

  // Group events by their recurring event ID
  // Google Calendar provides a recurringEventId for instances of recurring events
  const eventGroups = new Map<string, ProcessedEvent[]>();

  events.forEach((event) => {
    // Use recurringEventId if available, otherwise use the event's own ID
    const groupKey = event.recurringEventId || event.id;

    if (!eventGroups.has(groupKey)) {
      eventGroups.set(groupKey, []);
    }
    eventGroups.get(groupKey)!.push(event);
  });

  // For each group, keep only the next future occurrence
  const filteredEvents: ProcessedEvent[] = [];

  eventGroups.forEach((group, _key) => {
    if (group.length === 1) {
      // Single occurrence - keep it if it's in the future or has a recurrence rule
      const event = group[0];
      if (event.start >= now || event.recurrenceRule) {
        filteredEvents.push(event);
      }
    } else {
      // Multiple occurrences - find the next one in the future
      const futureEvents = group
        .filter((e) => e.start > now)
        .sort((a, b) => a.start.getTime() - b.start.getTime());

      if (futureEvents.length > 0) {
        // Add only the next occurrence
        filteredEvents.push(futureEvents[0]);
      } else {
        // If all occurrences are in the past, check if event has recurrence rule
        // In that case, we should calculate the next occurrence from the rule
        const eventWithRRule = group.find((e) => e.recurrenceRule);
        if (eventWithRRule) {
          filteredEvents.push(eventWithRRule);
        }
      }
    }
  });

  return filteredEvents;
};

/**
 * Calculate the next occurrence date for a recurring event
 * and attach it to the event object
 *
 * @param event - Event to process
 * @returns Event with nextOccurrence field populated
 */
export const enrichEventWithNextOccurrence = (
  event: ProcessedEvent,
): ProcessedEvent => {
  const nextOccurrence = getNextOccurrence(event);

  return {
    ...event,
    nextOccurrence: nextOccurrence || undefined,
    isRecurring: isRecurringEvent(event),
    originalStartDate: event.start,
  };
};

/**
 * Process an array of events to calculate next occurrences for all recurring events
 *
 * @param events - Events to process
 * @returns Events with nextOccurrence data
 */
export const enrichEventsWithNextOccurrences = (
  events: ProcessedEvent[],
): ProcessedEvent[] => {
  return events.map(enrichEventWithNextOccurrence);
};

/**
 * Get a human-readable description of the recurrence pattern
 *
 * @param rruleString - RRULE string
 * @returns Human-readable description (e.g., "Every week on Thursday")
 */
export const getRecurrenceDescription = (rruleString: string): string => {
  try {
    const rule = rrulestr(
      rruleString.startsWith("RRULE:") ? rruleString : `RRULE:${rruleString}`,
    );
    return rule.toText();
  } catch (error) {
    console.error("Failed to generate recurrence description:", error);
    return "Recurring event";
  }
};

/**
 * Check if event should be displayed based on recurrence and time filter
 *
 * @param event - Event to check
 * @param filterStartDate - Start of filter range
 * @param filterEndDate - End of filter range
 * @returns True if event should be displayed
 */
export const shouldDisplayRecurringEvent = (
  event: ProcessedEvent,
  filterStartDate: Date,
  filterEndDate: Date,
): boolean => {
  // For non-recurring events, simple date range check
  if (!isRecurringEvent(event)) {
    return event.start >= filterStartDate && event.start <= filterEndDate;
  }

  // For recurring events, check if next occurrence is within filter range
  const nextOccurrence = getNextOccurrence(event, filterStartDate);

  if (!nextOccurrence) {
    return false; // No future occurrences
  }

  return nextOccurrence >= filterStartDate && nextOccurrence <= filterEndDate;
};
